import { useEffect, useState } from 'react';
import { IconBrandWhatsapp, IconMail, IconMessage } from '@tabler/icons-react';
import {
  Button,
  Group,
  IconButton,
  Popover,
  Text,
} from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { AsyncActionButton } from '../../components/AsyncActionButton';
import {
  StaffMessageComposer,
  type StaffContactChannel,
} from '../../components/StaffMessageComposer';
import {
  type StaffAccountCreator,
} from '../../components/StaffAccountDisplay';
import { type StaffSessionRow } from '../../components/StaffSessionsPanel';
import { type StaffReportingChain } from '../../components/StaffReportingGraph';
import { UserRolePill } from '../../components/UserRolePill';
import { apiGet } from '../../api';
import { type StaffActivityRow } from '../../staffActivityTimeline';
import styles from './usersPageHelpers.module.css';
import {
  mapWhatsAppExistsPayload,
  whatsAppExistsStatusLabel,
  type WhatsAppExistsPayload,
  type WhatsAppExistsStatus,
} from './whatsappExistsStatus';

export const USER_CREATE_WIZARD_LABELS = [
  'Nom',
  'E-mail',
  'Photo',
  'Mobile',
  'Rôles',
  'Actif',
] as const;
export const USER_CREATE_STEP = {
  name: 0,
  email: 1,
  photo: 2,
  phone: 3,
  roles: 4,
  active: 5,
} as const;
export const USER_CREATE_LAST_STEP = USER_CREATE_WIZARD_LABELS.length - 1;

export {
  staffCreateEmailConflictFromError,
  staffCreateEmailFormatError,
  staffCreateEmailIdentityError,
  staffEmailTakenInRows,
  staffFullNameDuplicateWarning,
} from './staffCreateEmailIdentity';

export {
  mapWhatsAppExistsPayload,
  whatsAppExistsStatusLabel,
  type WhatsAppExistsPayload,
  type WhatsAppExistsStatus,
} from './whatsappExistsStatus';
/** ASCII email-local token from a name part (accents stripped). */
export function staffEmailLocalToken(part: string): string {
  return part
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '');
}

/** Suggested local-parts from prénom + nom (e.g. prenom.nom). */
export function staffEmailLocalSuggestions(firstName: string, lastName: string): string[] {
  const first = staffEmailLocalToken(firstName);
  const last = staffEmailLocalToken(lastName);
  const out: string[] = [];
  const push = (s: string) => {
    if (s && !out.includes(s)) out.push(s);
  };
  if (first && last) {
    push(`${first}.${last}`);
    push(`${first}${last}`);
    push(`${last}.${first}`);
    push(`${first[0]}.${last}`);
    push(`${first}.${last[0]}`);
  } else if (first) {
    push(first);
  } else if (last) {
    push(last);
  }
  return out;
}

export const USER_DETAIL_TABS = ['summary', 'roles', 'activity', 'history'] as const;
export type UserDetailTab = (typeof USER_DETAIL_TABS)[number];
/** Inline edit atom on user detail (mirrors product `ProductDetailInlineField`). */
export type UserDetailSection = 'firstName' | 'lastName' | 'phone' | 'roles';

export type UserRow = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone?: string | null;
  profilePictureKey?: string | null;
  profilePictureUrl?: string | null;
  active: boolean;
  createdAt: string;
  roles: string[];
  deletedAt?: string | null;
  createdBy: StaffAccountCreator | null;
  lastConnectedAt: string | null;
  lastSeenAt: string | null;
  isConnected: boolean;
  contactChannels?: StaffContactChannel[];
};

export type UserDetail = UserRow & {
  firstLoginAt?: string | null;
  mustChangePassword?: boolean;
  sessions?: StaffSessionRow[];
  lastSession?: {
    lastSeenAt: string | null;
    ip: string | null;
    userAgent: string | null;
    deviceType?: string | null;
    deviceLabel?: string | null;
    userAgentSummary?: string | null;
    locationLabel: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
    latitude: number | null;
    longitude: number | null;
  } | null;
  lastStaffActivity?: StaffActivityRow | null;
  reportingChain?: StaffReportingChain | null;
  phone?: string | null;
  contactChannels?: StaffContactChannel[];
};

export function RowMessagePopover({
  userId,
  token,
  channels,
  initialChannel,
  lockChannel,
  disabled,
  onSent,
  kind,
  triggerDisabled,
  triggerDisabledReason,
  messagesPath,
  whatsappExistsPath,
  bodyPlaceholder,
}: {
  userId: string;
  token: string | null;
  channels: StaffContactChannel[];
  initialChannel: 'EMAIL' | 'WHATSAPP';
  lockChannel: boolean;
  disabled?: boolean;
  onSent?: () => void;
  /** `message` = channel picker + IconMessage (detail page). */
  kind: 'email' | 'whatsapp' | 'message';
  triggerDisabled?: boolean;
  triggerDisabledReason?: string;
  messagesPath?: string;
  whatsappExistsPath?: string;
  bodyPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [waCheck, setWaCheck] = useState<
    'idle' | 'loading' | WhatsAppExistsStatus
  >('idle');

  useEffect(() => {
    if (!open) return;
    if (kind !== 'whatsapp') return;
    if (waCheck !== 'idle') return;
    if (!token) return;
    setWaCheck('loading');
    void apiGet<{ data: WhatsAppExistsPayload }>(
      whatsappExistsPath ?? `/admin/users/${userId}/whatsapp-exists`,
      token,
    )
      .then((r) => {
        setWaCheck(mapWhatsAppExistsPayload(unwrapData(r) as WhatsAppExistsPayload));
      })
      .catch(() => setWaCheck('error'));
  }, [open, kind, waCheck, token, userId, whatsappExistsPath]);

  const waBlocked =
    kind === 'whatsapp' &&
    (waCheck === 'loading' ||
      waCheck === 'no' ||
      waCheck === 'unconfigured' ||
      waCheck === 'error');

  const triggerIcon =
    kind === 'whatsapp' ? (
      <IconBrandWhatsapp size={16} stroke={1.8} />
    ) : kind === 'email' ? (
      <IconMail size={16} stroke={1.8} />
    ) : (
      <IconMessage size={16} stroke={1.8} />
    );

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      portal
      align="end"
      portalMinWidth={320}
      portalMaxWidth={380}
      portalEstimatedHeight={kind === 'message' ? 420 : 340}
      /* Above portaled CatalogHoverTip cards (+40); below toast (10050). */
      portalZIndex="calc(var(--wp-z-popover-over-modal, 10000) + 48)"
      target={
        <IconButton
          type="button"
          size="xs"
          variant="ghost"
          aria-label="Contacter le collaborateur"
          aria-expanded={open}
          disabled={Boolean(triggerDisabled)}
          title={triggerDisabled ? triggerDisabledReason : 'Contacter le collaborateur'}
        >
          {triggerIcon}
        </IconButton>
      }
    >
      <div className={styles.composerPanel}>
        {kind === 'message' ? (
          <Text size="xs" c="dimmed" fw={600} className={styles.composerTitle}>
            Contacter le collaborateur
          </Text>
        ) : null}
        {kind === 'whatsapp' && waCheck === 'loading' ? (
          <Group gap="xs" align="center" wrap="nowrap" className={styles.waHint}>
            <IconBrandWhatsapp size={16} stroke={1.7} aria-hidden className={styles.waIconBusy} />
            <Text size="xs" c="dimmed">
              Vérification du numéro…
            </Text>
          </Group>
        ) : null}
        {kind === 'whatsapp' &&
        (waCheck === 'no' || waCheck === 'unconfigured' || waCheck === 'error') ? (
          <Group gap="xs" align="flex-start" wrap="nowrap" className={styles.waHint}>
            <IconBrandWhatsapp size={16} stroke={1.7} aria-hidden className={styles.waIconMuted} />
            <Text size="xs" c="dimmed">
              {whatsAppExistsStatusLabel(waCheck)}
            </Text>
          </Group>
        ) : null}
        <StaffMessageComposer
          userId={userId}
          token={token}
          disabled={disabled || waBlocked}
          contactChannels={channels}
          initialChannel={initialChannel}
          lockChannel={lockChannel}
          messagesPath={messagesPath}
          bodyPlaceholder={bodyPlaceholder}
          onSent={() => {
            setOpen(false);
            onSent?.();
          }}
        />
      </div>
    </Popover>
  );
}

export function userSectionEditActions(onCancel: () => void, onSave: () => Promise<void>) {
  return (
    <>
      <Button type="button" variant="ghost" onClick={onCancel}>
        Annuler
      </Button>
      <AsyncActionButton type="button" variant="primary" onAction={onSave} pendingLabel="Enregistrement…">
        Enregistrer
      </AsyncActionButton>
    </>
  );
}

export function formatUserDisplayTitle(detail: Pick<UserRow, 'firstName' | 'lastName' | 'email'>): string {
  return formatUserFullName(detail) ?? detail.email;
}

export function formatUserFullName(r: Pick<UserRow, 'firstName' | 'lastName'>): string | null {
  const first = r.firstName?.trim() ?? '';
  const last = r.lastName?.trim() ?? '';
  const full = [first, last].filter(Boolean).join(' ');
  return full || null;
}

export function UserRoleBadges({ roles }: { roles: string[] }) {
  if (!roles.length) {
    return (
      <Text size="md" c="dimmed">
        —
      </Text>
    );
  }
  const sorted = [...roles].sort((a, b) => a.localeCompare(b, 'fr'));
  return (
    <Group gap={6} wrap="wrap">
      {sorted.map((code) => (
        <UserRolePill key={code} role={code} />
      ))}
    </Group>
  );
}
